// PeekPanel.jsx

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

// Context
import { useNodePeek } from 'context/NodePeekContext';
import { useNodeTypes } from 'context/NodeTypesContext';
import { useTheme } from 'context/ThemeContext';
import { useUser } from 'context/UserContext';

// Device Detection
import { useCanDockPeek, useIsPlaybookFocusEmbed } from 'hooks/useDeviceDetect';

// Utilities
import { getSingularLabel } from 'utilities/Languages';
import { canUser, PermissionAction } from 'utilities/NodeSecurity';
import { resolveDisplaySchemaFields } from 'views/utils/displaySchemaFields';

// Hooks
import useActorCapabilities from 'hooks/useActorCapabilities';
import useNodeAddRelated from 'node/addrelated/useNodeAddRelated';
import useNodeContributionHandlers from 'node/hooks/useNodeContributionHandlers';
import useNodeContributionState from 'node/hooks/useNodeContributionState';
import useNodeData from 'node/hooks/useNodeData';
import useNodeEnumPills from 'node/hooks/useNodeEnumPills';
import usePeekLeadingPills from 'node/peek/hooks/usePeekLeadingPills';
import { isProvisional } from 'node/provisional/provisionalNodeSession';
import useNodeOpenIntent from 'views/hooks/useNodeOpenIntent';

// CSS
import './PeekPanel.css';

// Components
import Button from 'components/button/Button';
import ContextMenu from 'components/contextmenu/ContextMenu';
import IconButton from 'components/iconbutton/IconButton';
import CameraModal from 'editor/nodeadd/camera/CameraModal';
import StructuralAncestryTrail from 'node/ancestry/StructuralAncestryTrail';
import NodeCoverField from 'node/form/fields/cover/NodeCoverField';
import NodeTitleButton from 'node/form/fields/title/NodeTitleButton';
import NodeHiddenFileInputs from 'node/inputs/NodeHiddenFileInputs';
import PeekPanelHeader from 'node/peek/header/PeekPanelHeader';
import PeekPanelTabs from 'node/peek/PeekPanelTabs';
import NodeMetadataFooter from 'node/shared/NodeMetadataFooter';
import { UNIFIED_TAB_KEYS } from 'node/tabs/unifiedNodeTabKeys';

const PEEK_DOCUMENT_TYPE_KEY = 'DOCUMENT';
const PEEK_FILE_TYPE_KEY = 'FILE';
const PEEK_PHOTO_TYPE_KEY = 'PHOTO';
const PEEK_VIDEO_TYPE_KEY = 'VIDEO';

const normalizePeekTypeKey = (value) => String(value || '').trim().toUpperCase();

/**
 * PeekPanel
 *
 * Renders a right-side peek panel for editing node metadata and previewing related items before full navigation.
 *
 * @param {boolean} isOpen - Whether the peek panel should be open.
 * @param {object|null} node - The peeked node object.
 * @param {boolean} isLoading - Whether peek data is loading.
 * @param {string} error - Error message shown when peek loading fails.
 * @param {boolean} canGoBack - Whether the peek history stack has a previous item.
 * @param {function} onBack - Called when the peek back button is pressed.
 * @param {function} onClose - Called when the panel should close.
 * @param {function} onTitleSaved - Called when the title is saved.
 * @param {function} onCoverSaved - Called when the cover field is saved.
 * @param {function} onLocationSaved - Called when a location field is saved.
 * @param {function} onSubtitleSaved - Called when the description is saved.
 * @param {function} onTagsSaved - Called when tags are saved.
 * @param {function} onNodeChange - Called for generic node field changes.
 * @param {function} onQuickActionSelect - Optional host handler for non-media quick actions (media uses PeekPanel's own camera/file inputs).
 *
 * @returns {ReactNode} The rendered peek panel.
 */
const PeekPanel = React.memo(({
  isOpen,
  node,
  isLoading,
  error,
  canGoBack,
  onBack,
  onClose,
  onTitleSaved,
  onCoverSaved,
  onLocationSaved,
  onSubtitleSaved,
  onTagsSaved,
  onNodeChange,
  onQuickActionSelect,
}) => {

  // ----- HOOKS ----- //

  const {
    canPeekGoBack,
    goBackPeek,
    openPeek,
    peekPanelTabKey,
    setPeekPanelTabKey,
  } = useNodePeek();

  const { openNode } = useNodeOpenIntent();

  // ----- CONTEXT ----- //

  const { getType, isReady } = useNodeTypes();
  const { theme } = useTheme();
  const { user, userSettings } = useUser();

  // ----- DEVICE DETECTION ----- //

  const canDockPeek = useCanDockPeek();
  const isPlaybookFocusEmbed = useIsPlaybookFocusEmbed();
  const compactPlaybookChrome = Boolean(
    isPlaybookFocusEmbed && peekPanelTabKey === UNIFIED_TAB_KEYS.WORKFLOW
  );

  useNodeData({
    currentNode: node,
    setCurrentNode: null,
    user,
  });

  // ----- STATE ----- //

  const [isMounted, setIsMounted] = useState(isOpen);
  const [isVisible, setIsVisible] = useState(isOpen);
  // Experimental: mirror of unified tabs for sections hamburger (feel test).
  const [sectionMenuTabs, setSectionMenuTabs] = useState([]);

  const [, setDefaultNode] = useState(null);
  const [, setEditorSaveMode] = useState(null);
  const [, setInitialChildren] = useState([]);
  const [, setInitialActiveNodeKey] = useState(null);
  const [, setIsUploading] = useState(false);
  const [pendingAutoTabTypeKey, setPendingAutoTabTypeKey] = useState('');

  // ----- REFS ----- //

  const photoInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const documentInputRef = useRef(null);
  const filesInputRef = useRef(null);
  const coverPickerRef = useRef(null);
  const sectionsMenuTriggerRef = useRef(null);

  // ----- HOOKS ----- //

  const {
    cameraOpen,
    cameraContentType,
    contributeParent,
    setContributeOpen,
    setContributeParent,
    setPreSelectedType,
    setCameraOpen,
    setCameraContentType,
  } = useNodeContributionState();

  const nodeTypesReady = isReady();

  // ----- MEMOS ----- //

  const nodeType = useMemo(() => {
    if (!nodeTypesReady) return null;

    const typeKey = typeof node?.type === 'string' ? node.type.trim() : '';
    if (!typeKey) return null;

    return getType(typeKey) || null;
  }, [getType, node?.type, nodeTypesReady]);

  const { canEdit } = useActorCapabilities();

  const canEditPeekNode = useMemo(
    () => Boolean(canEdit) && canUser(PermissionAction.EDIT, node, user),
    [canEdit, node, user],
  );

  const isProvisionalPeekNode = isProvisional(node);

  // While provisional, form/metadata only — no related creates or contributions.
  const canAddRelatedPeekNode = canEditPeekNode && !isProvisionalPeekNode;

  const originFieldsReadOnly = !canEditPeekNode;

  // ----- HOOKS ----- //

  const enumPills = useNodeEnumPills({
    node,
    nodeTypesReady,
    language: userSettings?.language || 'en',
    includeEmpty: true,
  });

  const leadingPills = usePeekLeadingPills(node);

  // ----- MEMOS ----- //

  const coverSchemaField = useMemo(() => {
    if (!nodeType) return null;
    return resolveDisplaySchemaFields(nodeType, { includeHidden: false })
      .find((field) => field?.type === 'cover') || null;
  }, [nodeType]);

  const hasCoverField = Boolean(coverSchemaField);

  const isCatalogOwnedCover = Boolean(
    String(node?.catalogId || '').trim()
    && coverSchemaField
    && coverSchemaField.workspaceField !== true
  );

  const hasCoverImage = useMemo(() => (
    typeof node?.coverUrl === 'string' && Boolean(node.coverUrl.trim())
  ), [node?.coverUrl]);

  const coverReadOnly = originFieldsReadOnly || isCatalogOwnedCover;

  const showAddCoverButton = Boolean(
    hasCoverField
    && node
    && !hasCoverImage
    && !coverReadOnly
    && !compactPlaybookChrome
  );

  const showBackButton = typeof canGoBack === 'boolean' ? canGoBack : canPeekGoBack;

  const activeSectionLabel = useMemo(() => {
    const activeKey = String(peekPanelTabKey || '').trim();
    if (!activeKey) return '';
    const match = sectionMenuTabs.find(
      (tab) => String(tab?.key || '').trim() === activeKey,
    );
    return String(match?.label || '').trim();
  }, [peekPanelTabKey, sectionMenuTabs]);

  const sectionsMenuTypeHeader = useMemo(() => {
    const label = getSingularLabel(
      nodeType,
      node?.type,
      userSettings?.language || 'en',
    );
    return String(label || node?.type || '').trim();
  }, [node?.type, nodeType, userSettings?.language]);

  // ----- HANDLERS ----- //

  const handleBackClick = useCallback(() => {
    if (typeof onBack === 'function') {
      onBack();
      return;
    }

    if (showBackButton) {
      goBackPeek?.();
    }
  }, [goBackPeek, onBack, showBackButton]);

  const handlePeekCoverSaved = useCallback((payload) => {
    if (typeof onCoverSaved !== 'function') return;
    onCoverSaved(node?.key, payload);
  }, [node?.key, onCoverSaved]);

  /**
   * Opens the cover file picker from the empty-state add button.
   */
  const handleAddCoverClick = useCallback(() => {
    coverPickerRef.current?.openPicker?.();
  }, []);

  const handlePeekRelatedNodeCreated = useCallback((createdNode) => {
    if (!createdNode?.key) return;

    openPeek(createdNode, {
      mode: isOpen ? 'push' : 'reset',
    });
  }, [isOpen, openPeek]);

  const queueAutoSelectRelatedTab = useCallback((typeKey) => {
    const normalizedTypeKey = normalizePeekTypeKey(typeKey);
    if (!normalizedTypeKey) return;
    setPendingAutoTabTypeKey(normalizedTypeKey);
  }, []);

  // ----- HOOKS ----- //

  const {
    handleQuickActionSelect,
    handlePhotoSelect,
    handleVideoSelect,
    handleDocumentSelect,
    handleFilesSelect,
    handleCameraContentCreated,
  } = useNodeContributionHandlers({
    node,
    user,
    navigate: null,
    setCurrentNode: null,
    setCurrentSection: null,
    contributeParent,
    setContributeOpen,
    setContributeParent,
    setPreSelectedType,
    setCameraOpen,
    setCameraContentType,
    setDefaultNode,
    setEditorSaveMode,
    setInitialChildren,
    setInitialActiveNodeKey,
    setIsUploading,
    photoInputRef,
    videoInputRef,
    documentInputRef,
    filesInputRef,
    onRelatedNodeCreated: handlePeekRelatedNodeCreated,
  });

  // ----- HANDLERS ----- //

  const handlePeekQuickActionSelect = useCallback((actionKey, options) => {
    const normalizedKey = String(actionKey || '').trim().toUpperCase();
    const isMediaAction = normalizedKey === 'CAMERA'
      || normalizedKey === 'SNAPSHOT'
      || normalizedKey === 'RECORDING'
      || normalizedKey === 'PHOTO'
      || normalizedKey === 'VIDEO'
      || normalizedKey === 'DOCUMENT'
      || normalizedKey === 'FILE'
      || normalizedKey === 'ATTACH';

    // Media actions must use PeekPanel's own inputs/CameraModal so captures
    // attach to the peeked node (and work when hosts omit onQuickActionSelect).
    if (isMediaAction) {
      if (
        normalizedKey === 'CAMERA'
        || normalizedKey === 'SNAPSHOT'
        || normalizedKey === 'PHOTO'
      ) {
        queueAutoSelectRelatedTab(PEEK_PHOTO_TYPE_KEY);
      } else if (normalizedKey === 'RECORDING' || normalizedKey === 'VIDEO') {
        queueAutoSelectRelatedTab(PEEK_VIDEO_TYPE_KEY);
      } else if (normalizedKey === 'DOCUMENT') {
        queueAutoSelectRelatedTab(PEEK_DOCUMENT_TYPE_KEY);
      } else if (normalizedKey === 'FILE' || normalizedKey === 'ATTACH') {
        queueAutoSelectRelatedTab(PEEK_FILE_TYPE_KEY);
      }

      handleQuickActionSelect(actionKey, options);
      return;
    }

    // Add New record types: create under the peeked node and open the new
    // item in Peek immediately (Attach Existing… remains the link path).
    const peekCreateOptions = {
      ...(options && typeof options === 'object' ? options : {}),
      openInPeek: true,
    };

    if (typeof onQuickActionSelect === 'function') {
      onQuickActionSelect(actionKey, peekCreateOptions);
      return;
    }

    handleQuickActionSelect(actionKey, peekCreateOptions);
  }, [handleQuickActionSelect, onQuickActionSelect, queueAutoSelectRelatedTab]);

  const handlePeekPhotoSelect = useCallback((event) => {
    const hasFiles = Array.isArray(event?.target?.files)
      ? event.target.files.length > 0
      : Boolean(event?.target?.files?.length);

    if (hasFiles) {
      queueAutoSelectRelatedTab(PEEK_PHOTO_TYPE_KEY);
    }

    handlePhotoSelect(event);
  }, [handlePhotoSelect, queueAutoSelectRelatedTab]);

  const handleTabsChange = useCallback((nextTabs) => {
    setSectionMenuTabs(Array.isArray(nextTabs) ? nextTabs : []);
  }, []);

  const {
    menuChildren: addRelatedMenuChildren,
    modals: addRelatedModals,
  } = useNodeAddRelated({
    node: canAddRelatedPeekNode ? node : null,
    onQuickActionSelect: canAddRelatedPeekNode ? handlePeekQuickActionSelect : undefined,
  });

  const sectionsMenuItems = (
    <>
      {sectionsMenuTypeHeader ? (
        <ContextMenu.Section label={sectionsMenuTypeHeader} />
      ) : null}
      {sectionMenuTabs.map((tab) => {
        const tabKey = String(tab?.key || '').trim();
        if (!tabKey) return null;
        const isActiveTab = tabKey === peekPanelTabKey;
        return (
          <ContextMenu.Item
            key={tabKey}
            iconKey={isActiveTab
              ? 'check'
              : (tab.iconKey || 'deployed_code')}
            label={tab.label || tabKey}
            onClick={() => {
              // Prefer unified selectTab (clears pending session restore).
              if (typeof tab.onClick === 'function') {
                tab.onClick();
                return;
              }
              setPeekPanelTabKey(tabKey);
            }}
          />
        );
      })}
      {canAddRelatedPeekNode && addRelatedMenuChildren.length > 0 ? (
        <>
          <ContextMenu.Separator />
          <ContextMenu.Item
            iconKey="add"
            label="Add related…"
          >
            {addRelatedMenuChildren}
          </ContextMenu.Item>
        </>
      ) : null}
    </>
  );

  // ----- EFFECTS ----- //

  useEffect(() => {
    setSectionMenuTabs([]);
  }, [node?.key]);

  // Keeps the panel mounted long enough for close transitions.
  useEffect(() => {
    let frame = null;
    let closeTimer = null;

    if (isOpen) {
      setIsMounted(true);
      frame = window.requestAnimationFrame(() => {
        setIsVisible(true);
      });
    } else {
      setIsVisible(false);
      closeTimer = window.setTimeout(() => {
        setIsMounted(false);
      }, 200);
    }

    return () => {
      if (frame != null) window.cancelAnimationFrame(frame);
      if (closeTimer != null) window.clearTimeout(closeTimer);
    };
  }, [isOpen]);

  // Closes panel when Escape is pressed while mounted.
  useEffect(() => {
    if (!isMounted) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose?.();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isMounted, onClose]);

  if (!isMounted) return null;

  // Wide desktop: dock into Main's peek slot (flex push).
  // Phone + tablet shell: fixed overlay so main content keeps usable width.
  const dockEl = canDockPeek && typeof document !== 'undefined'
    ? document.getElementById('main-peek-slot')
    : null;
  const isDocked = Boolean(dockEl);

  // ----- RENDER ----- //

  const panel = (
    <div
      className={[
        'peek-panel-container',
        isVisible ? 'is-open' : '',
        isDocked ? 'is-docked' : '',
      ].filter(Boolean).join(' ')}
    >

      {!isDocked ? (
        <div className="peek-panel-wide-veil" aria-hidden="true" />
      ) : null}

      <aside className="peek-panel">

        {/* Background temporarily disabled
        <Background node={node || null} />
        */}

        <div
          className={`peek-panel-content${compactPlaybookChrome ? ' is-playbook-focus' : ''}`}
        >
          <PeekPanelHeader
            showBackButton={showBackButton}
            node={node}
            onBackClick={handleBackClick}
            onClose={onClose}
            hideMenuPanel={!canEditPeekNode || compactPlaybookChrome}
          />

          {/* STRUCTURAL ANCESTRY — above cover + title */}
          <div className="peek-panel-ancestry">
            <StructuralAncestryTrail
              node={node}
              variant="compact"
              preferPeek
            />
          </div>

          {hasCoverField && hasCoverImage && node ? (
            <div className="peek-panel-cover">
              <NodeCoverField
                node={node}
                readOnly={coverReadOnly}
                onCoverSaved={coverReadOnly ? undefined : handlePeekCoverSaved}
              />
            </div>
          ) : null}

          {showAddCoverButton ? (
            <NodeCoverField
              ref={coverPickerRef}
              node={node}
              emptyMode="none"
              onCoverSaved={handlePeekCoverSaved}
            />
          ) : null}

          {showAddCoverButton ? (
            <div className="peek-panel-add-cover">
              <Button
                iconKey="photo"
                label="Add Cover Photo"
                title="Add cover photo"
                backgroundColor="transparent"
                borderColor="transparent"
                foregroundColor={theme.foregroundMuted}
                hoverBackgroundColor={theme.buttonHoverBackgroundColor}
                hoverForegroundColor={theme.buttonHoverLabelColor}
                onClick={handleAddCoverClick}
              />
            </div>
          ) : null}

          <div className="peek-panel-title-row">
            {sectionMenuTabs.length > 0 ? (
              <div className="peek-panel-sections-menu">
                <span
                  ref={sectionsMenuTriggerRef}
                  className="peek-panel-sections-menu-trigger"
                >
                  <IconButton
                    iconKey="menu"
                    title="Sections"
                    size={42}
                    iconSize={42}
                    color={theme.foregroundMuted}
                  />
                </span>
                <ContextMenu
                  triggerMode="click"
                  triggerRef={sectionsMenuTriggerRef}
                  zIndex={1400}
                >
                  {sectionsMenuItems}
                </ContextMenu>
              </div>
            ) : null}

            <div className="peek-panel-title-editor">
              <NodeTitleButton
                node={node}
                autoFocus={false}
                autoSelect={false}
                // Peek titles stay single-line so open-animation autosize
                // cannot leave a tall empty text box.
                multiline={false}
                expandable={false}
                readOnly={originFieldsReadOnly}
                inputStyle={{
                  width: '100%',
                  maxWidth: '100%',
                  fontSize: '18pt',
                  paddingTop: hasCoverImage ? 4 : 6,
                  paddingLeft: 4,
                }}
                hybrid
                onTitleSaved={originFieldsReadOnly ? undefined : onTitleSaved}
              />
            </div>

            {node ? (
              <div className="peek-panel-open-button">
                <Button
                  iconKey="open_in_new"
                  title="Open"
                  height="32px"
                  width="32px"
                  padding="0"
                  iconSize="18px"
                  onClick={() => openNode(node, { preferPeek: false })}
                />
              </div>
            ) : null}
          </div>

          {!compactPlaybookChrome ? (
            <NodeMetadataFooter
              node={node}
              leadingPills={leadingPills}
              enumPills={enumPills}
              addon="peek"
              canEdit={!originFieldsReadOnly}
              onNodeChange={originFieldsReadOnly ? undefined : onNodeChange}
              language={userSettings?.language || 'en'}
            />
          ) : null}

          {activeSectionLabel && !compactPlaybookChrome ? (
            <div className="peek-panel-active-section">
              {activeSectionLabel}
            </div>
          ) : null}

          <div className="peek-panel-body">

            {isLoading && (
              <div className="peek-panel-message">
                Loading...
              </div>
            )}

            {!isLoading && error && (
              <div className="peek-panel-message">
                {error}
              </div>
            )}

            {!isLoading && !error && node && (
              <>

                <PeekPanelTabs
                  node={node}
                  nodeType={nodeType}
                  canEditNode={canEditPeekNode}
                  isOpen={isOpen}
                  pendingAutoTabTypeKey={pendingAutoTabTypeKey}
                  setPendingAutoTabTypeKey={setPendingAutoTabTypeKey}
                  onCoverSaved={onCoverSaved}
                  onLocationSaved={onLocationSaved}
                  onNodeChange={onNodeChange}
                  onQuickActionSelect={
                    canAddRelatedPeekNode ? handlePeekQuickActionSelect : undefined
                  }
                  onSubtitleSaved={onSubtitleSaved}
                  onTagsSaved={onTagsSaved}
                  onTabsChange={handleTabsChange}
                />
              </>
            )}
          </div>
        </div>

        {canAddRelatedPeekNode ? addRelatedModals : null}

        <NodeHiddenFileInputs
          photoInputRef={photoInputRef}
          videoInputRef={videoInputRef}
          documentInputRef={documentInputRef}
          filesInputRef={filesInputRef}
          onDocumentSelect={handleDocumentSelect}
          onFilesSelect={handleFilesSelect}
          onPhotoSelect={handlePeekPhotoSelect}
          onVideoSelect={handleVideoSelect}
        />

        <CameraModal
          isOpen={cameraOpen}
          setOpen={setCameraOpen}
          contentType={cameraContentType}
          onContentCreated={handleCameraContentCreated}
        />

      </aside>
    </div>
  );

  if (dockEl) {
    return createPortal(panel, dockEl);
  }

  return panel;
});

// Dev Tools
PeekPanel.displayName = 'PeekPanel';

export default PeekPanel;
